import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { getCachedUser, setCachedUser, loadRoles, peekRoles, loadAffiliateRow, peekAffiliateRow } from "@/lib/session-cache";

export type AffiliateRole = "admin" | "affiliate" | "member" | null;

export interface AffiliateProfile {
  id: string;
  user_id: string;
  code: string;
  full_name: string;
  email: string;
  commission_percent: number;
  iban: string | null;
  payout_email: string | null;
  active: boolean;
  must_change_password: boolean;
}

const roleFrom = (roles: string[]): AffiliateRole =>
  roles.includes("admin") ? "admin" : roles.includes("affiliate") ? "affiliate" : "member";

/** Synchronous snapshot from cache so remounts (page navigation) render instantly. */
const snapshot = () => {
  const u = getCachedUser();
  if (u === undefined) return null;
  if (u === null) return { user: null, role: null as AffiliateRole, affiliate: null, ready: true };
  const roles = peekRoles(u.id);
  if (!roles) return null;
  const role = roleFrom(roles);
  const needsAff = role === "admin" || role === "affiliate";
  const aff = needsAff ? peekAffiliateRow<AffiliateProfile>(u.id) : null;
  if (needsAff && aff === undefined) return null;
  return { user: u, role, affiliate: aff ?? null, ready: true };
};

export const useAffiliate = () => {
  const snap = snapshot();
  const [user, setUser] = useState<User | null | undefined>(snap ? snap.user : undefined);
  const [role, setRole] = useState<AffiliateRole>(snap?.role ?? null);
  const [affiliate, setAffiliate] = useState<AffiliateProfile | null>(snap?.affiliate ?? null);
  const [loading, setLoading] = useState(!snap);

  useEffect(() => {
    let alive = true;
    const refresh = async (u: User | null) => {
      if (!u) {
        if (!alive) return;
        setRole(null); setAffiliate(null); setLoading(false);
        return;
      }
      try {
        const r = roleFrom(await loadRoles(u.id));
        const aff = r === "admin" || r === "affiliate" ? await loadAffiliateRow<AffiliateProfile>(u.id) : null;
        if (!alive) return;
        setRole(r); setAffiliate(aff);
      } catch {
        if (alive) { setRole("member"); setAffiliate(null); }
      } finally {
        if (alive) setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;
      const prev = getCachedUser();
      setCachedUser(u);
      setUser(u);
      // Token refreshes for the same user don't need a reload.
      if (prev && u && prev.id === u.id && event !== "SIGNED_IN") return;
      setTimeout(() => refresh(u), 0);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setCachedUser(u);
      setUser(u);
      refresh(u);
    }).catch(() => {
      if (alive) { setUser(null); setLoading(false); }
    });
    return () => { alive = false; subscription.unsubscribe(); };
  }, []);

  return { user, role, affiliate, loading, isAdmin: role === "admin", isAffiliate: role === "affiliate" };
};
